import { generateChatPath } from '@bettermode/common/utils'
import { Chat, ChatType, Member } from '@tribeplatform/gql-client/types'
import { dayjs } from '@tribeplatform/react-components/common/lib'

import { I18nShape } from '../i18n/types.js'
import { getLastMessageAuthorName } from './Chat/utils/chat.utils.js'

export interface ChatDetails {
  chatId: string
  type: ChatType
  name: string
  imageUrl: string | null
  link: string
  peerMemberId?: string
}

interface GetChatDetailsParams {
  chat: Chat
  authMember?: Member
  intl: I18nShape
}

export const getChatDetails = (params: GetChatDetailsParams): ChatDetails => {
  const { chat, authMember, intl } = params
  const chatId = chat._id
  const link = generateChatPath(chat._id)
  const { type, lastMessage } = chat
  const lastMessagePreview = previewHtmlContent(lastMessage?.preview)
  const lastMessageTimestamp = lastMessage?.sentAt
  const lastMessageAuthorName = getLastMessageAuthorName({
    lastMessage: chat.lastMessage,
    intl,
    authMember,
  })

  const commonProps = {
    chatId,
    type,
    link,
    lastMessageAuthorName,
    lastMessagePreview,
    lastMessageTimestamp,
  }

  if (chat.type === ChatType.Group) {
    return {
      ...commonProps,
      name: chat.name,
      imageUrl: chat.avatarUrl,
    }
  }

  // The other participnt is null for the messages that user send themselves
  const participant = chat.otherParticipant ?? chat.currentParticipant

  return {
    ...commonProps,
    name: participant.name ?? participant.handle,
    imageUrl: participant.avatarUrl,
    peerMemberId: participant.memberId,
  }
}

export const formatMessageTimestamp = (timestamp: string) => {
  return dayjs(timestamp).fromNow()
}

export const getTimestampUpdateInterval = (timestamp: string) => {
  const diffInDays = dayjs().diff(dayjs(timestamp), 'day')
  if (diffInDays >= 1) {
    return 1000 * 60 * 60 * 24 // one day in milliseconds
  }
  const diffInHours = dayjs().diff(dayjs(timestamp), 'hour')
  if (diffInHours >= 1) {
    return 1000 * 60 * 60 // one hour in milliseconds
  }
  return 1000 * 60 // one minute in milliseconds
}

export const previewHtmlContent = (original: string): string => {
  try {
    const parsed = new DOMParser().parseFromString(original, 'text/html')
    if (parsed.body.children.length === 0) {
      // It’s a plain text content
      return original
    }
    return Array.from(parsed.body.children).find(
      child => !!child?.textContent.trim(),
    )?.textContent
  } catch (e) {
    return ''
  }
}
